/**
 * Numbers — phone number management (§11), at /numbers.
 *
 * Owned numbers table with inline agent assign/unassign (PATCH
 * /api/numbers/{id}) and release-with-confirm (DELETE). Buy flow: country
 * picker restricted to instant-provision countries (US/CA/GB/AU — everything
 * else needs a regulatory bundle with human review, backlogged), area-code
 * search against GET /api/numbers/available, buy buttons that purchase AND
 * wire the voice webhook in one step. Typing +91/IN surfaces the
 * India-specific limitation instead of an opaque Twilio error.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { formatDate, formatPhone } from "../lib/format";
import type { AgentSummary, AvailableNumber, PhoneNumber } from "../lib/types";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { Input } from "../components/Input";
import { Modal } from "../components/Modal";
import { Select } from "../components/Select";
import { Spinner } from "../components/Spinner";
import { Table, TBody, TD, TH, THead, TR } from "../components/Table";

/** §11: only instant-provision countries in v1 — no regulatory bundles. */
const COUNTRIES = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
];

function isIndiaQuery(input: string): boolean {
  const trimmed = input.trim();
  return /^\+91/.test(trimmed) || /^\+?91$/.test(trimmed) || /^in(dia)?$/i.test(trimmed);
}

function errText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export default function Numbers() {
  const [numbers, setNumbers] = useState<PhoneNumber[] | null>(null);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Assign / release state
  const [rowError, setRowError] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [releaseTarget, setReleaseTarget] = useState<PhoneNumber | null>(null);
  const [releasing, setReleasing] = useState(false);

  // Buy flow state
  const [country, setCountry] = useState("US");
  const [areaCode, setAreaCode] = useState("");
  const [results, setResults] = useState<AvailableNumber[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [buying, setBuying] = useState<string | null>(null);
  const [buyError, setBuyError] = useState<string | null>(null);

  // Import — adopt a number the Twilio account already owns (e.g. bought in the
  // Twilio console, which points it at Twilio's demo TwiML until we repoint it).
  const [importE164, setImportE164] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importNotice, setImportNotice] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([api.numbers.list(controller.signal), api.agents.list(controller.signal)])
      .then(([owned, agentList]) => {
        setNumbers(owned);
        setAgents(agentList);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(errText(err, "Could not load your numbers."));
      });
    return () => controller.abort();
  }, []);

  const agentOptions = useMemo(
    () => agents.map((agent) => ({ value: agent.id, label: agent.name })),
    [agents],
  );

  // -------------------------------------------------------------------------
  // Owned numbers: assign / release
  // -------------------------------------------------------------------------

  async function handleAssign(number: PhoneNumber, agentId: string): Promise<void> {
    const nextAgentId = agentId || null;
    const previous = numbers;
    setRowError(null);
    setAssigningId(number.id);
    // Optimistic — roll back and surface the error inline on failure.
    setNumbers((prev) =>
      prev ? prev.map((n) => (n.id === number.id ? { ...n, agent_id: nextAgentId } : n)) : prev,
    );
    try {
      const updated = await api.numbers.assign(number.id, nextAgentId);
      setNumbers((prev) => (prev ? prev.map((n) => (n.id === number.id ? updated : n)) : prev));
    } catch (err: unknown) {
      setNumbers(previous);
      setRowError(
        errText(err, `Could not update ${formatPhone(number.e164)}. Try again.`),
      );
    } finally {
      setAssigningId(null);
    }
  }

  async function handleRelease(): Promise<void> {
    if (!releaseTarget) return;
    setReleasing(true);
    setRowError(null);
    try {
      await api.numbers.release(releaseTarget.id);
      setNumbers((prev) => (prev ? prev.filter((n) => n.id !== releaseTarget.id) : prev));
      setReleaseTarget(null);
    } catch (err: unknown) {
      setRowError(errText(err, "Could not release the number. Try again."));
      setReleaseTarget(null);
    } finally {
      setReleasing(false);
    }
  }

  // -------------------------------------------------------------------------
  // Buy flow: search + purchase
  // -------------------------------------------------------------------------

  async function handleSearch(): Promise<void> {
    setSearching(true);
    setSearchError(null);
    setBuyError(null);
    setResults(null);
    try {
      const digits = areaCode.replace(/\D/g, "");
      const found = await api.numbers.search(
        country,
        digits.length >= 2 && digits.length <= 5 ? digits : undefined,
      );
      setResults(found);
    } catch (err: unknown) {
      setSearchError(errText(err, "Number search failed. Try a different area code."));
    } finally {
      setSearching(false);
    }
  }

  async function handleBuy(candidate: AvailableNumber): Promise<void> {
    setBuying(candidate.phone_number);
    setBuyError(null);
    try {
      const bought = await api.numbers.buy(candidate.phone_number, candidate.country || country);
      setNumbers((prev) => (prev ? [bought, ...prev] : [bought]));
      setResults((prev) => (prev ? prev.filter((r) => r.phone_number !== candidate.phone_number) : prev));
    } catch (err: unknown) {
      setBuyError(
        errText(
          err,
          `Could not buy ${formatPhone(candidate.phone_number)}. It may have just been taken — search again.`,
        ),
      );
    } finally {
      setBuying(null);
    }
  }

  async function handleImport(): Promise<void> {
    const e164 = importE164.trim();
    if (!/^\+[1-9]\d{7,14}$/.test(e164)) {
      setImportError("Enter the number in E.164 form, e.g. +17853478894.");
      return;
    }
    setImporting(true);
    setImportError(null);
    setImportNotice(null);
    try {
      const imported = await api.numbers.import(e164);
      setNumbers((prev) => (prev ? [imported, ...prev] : [imported]));
      setImportE164("");
      setImportNotice(
        `${formatPhone(imported.e164)} imported. Its Twilio webhooks now point here — assign it to an agent below.`,
      );
    } catch (err: unknown) {
      setImportError(
        errText(err, `Could not import ${formatPhone(e164)}. Check the number and try again.`),
      );
    } finally {
      setImporting(false);
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loadError) {
    return (
      <div className="flex flex-col gap-3">
        <h1 className="font-display text-xl tracking-display text-bright">Phone numbers</h1>
        <p className="text-sm text-danger">{loadError}</p>
      </div>
    );
  }

  if (numbers === null) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  const indiaHint = isIndiaQuery(areaCode);

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="font-display text-xl tracking-display text-bright">Phone numbers</h1>
        <p className="mt-1 text-sm text-muted">
          Buy a number, point it at an agent, and it answers immediately.
        </p>
      </header>

      {rowError && (
        <p role="alert" className="rounded border border-danger/60 bg-surface px-3 py-2 text-sm text-danger">
          {rowError}
        </p>
      )}

      {/* Import an already-owned number */}
      <section className="flex flex-col gap-2 rounded border border-line p-4">
        <h2 className="font-display text-sm tracking-display text-bright">
          Already have a number?
        </h2>
        <p className="text-xs text-muted">
          If you bought it in the Twilio console it still points at Twilio&rsquo;s demo greeting.
          Importing it here repoints its webhooks at this platform. No charge — it&rsquo;s already
          yours.
        </p>
        <div className="flex flex-wrap items-start gap-2">
          <Input
            mono
            value={importE164}
            placeholder="+17853478894"
            aria-label="Phone number to import, in E.164 format"
            onChange={(event) => setImportE164(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !importing) void handleImport();
            }}
          />
          <Button
            variant="primary"
            loading={importing}
            disabled={!importE164.trim()}
            onClick={() => void handleImport()}
          >
            Import number
          </Button>
        </div>
        {importError && (
          <p role="alert" className="text-xs text-danger">
            {importError}
          </p>
        )}
        {importNotice && <p className="text-xs text-body">{importNotice}</p>}
      </section>

      {/* Owned numbers */}
      {numbers.length === 0 ? (
        <EmptyState
          title="No numbers yet"
          description="Import a number you already own above, or search below to buy one."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Number</TH>
              <TH>Country</TH>
              <TH>Assigned agent</TH>
              <TH>Added</TH>
              <TH align="right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {numbers.map((number) => (
              <TR key={number.id}>
                <TD mono>{formatPhone(number.e164)}</TD>
                <TD>{number.country ?? "—"}</TD>
                <TD>
                  <Select
                    value={number.agent_id ?? ""}
                    onChange={(e) => void handleAssign(number, e.target.value)}
                    disabled={assigningId === number.id}
                    aria-label={`Agent for ${formatPhone(number.e164)}`}
                    className="w-52"
                  >
                    <option value="">Unassigned</option>
                    {agentOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </TD>
                <TD mono>{formatDate(number.created_at)}</TD>
                <TD align="right">
                  <Button variant="danger" size="sm" onClick={() => setReleaseTarget(number)}>
                    Release
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
      {numbers.length > 0 && agents.length === 0 && (
        <p className="-mt-3 text-xs text-muted">
          You don't have any agents yet —{" "}
          <Link to="/agents/new" className="underline transition-colors hover:text-bright">
            create one
          </Link>{" "}
          to answer these numbers.
        </p>
      )}

      {/* Buy flow */}
      <section className="flex flex-col gap-3 rounded border border-line bg-surface p-4">
        <h2 className="font-display text-base tracking-display text-bright">Buy a number</h2>

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Country</span>
            <Select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              options={COUNTRIES}
              className="w-48"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Area code (optional)</span>
            <Input
              mono
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value)}
              placeholder="415"
              className="w-32"
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSearch();
              }}
            />
          </label>
          <Button variant="primary" loading={searching} onClick={() => void handleSearch()}>
            Search
          </Button>
        </div>

        <p className="text-xs text-muted">
          Numbers in these countries provision instantly. Other countries
          require regulatory review (address and identity bundles with human
          approval) and aren't available here yet.
        </p>

        {indiaHint && (
          <p className="rounded border border-warning/60 bg-surface-raised px-3 py-2 text-xs text-warning">
            Looking for India? Twilio doesn't support domestic Indian voice
            numbers — inbound domestic reachability is unavailable, and calls to
            Indian numbers must originate from a non-Indian number. Domestic
            Indian calling needs an Indian provider (Exotel, Ozonetel, Plivo
            India) and isn't supported yet.
          </p>
        )}

        {searchError && (
          <p role="alert" className="text-sm text-danger">
            {searchError}
          </p>
        )}
        {buyError && (
          <p role="alert" className="text-sm text-danger">
            {buyError}
          </p>
        )}

        {results !== null &&
          (results.length === 0 ? (
            <p className="text-sm text-muted">
              No numbers found{areaCode ? ` for area code ${areaCode}` : ""}. Try
              another area code or leave it blank.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Number</TH>
                  <TH>Location</TH>
                  <TH>Capabilities</TH>
                  <TH align="right" />
                </TR>
              </THead>
              <TBody>
                {results.map((candidate) => {
                  const location =
                    [candidate.locality, candidate.region].filter(Boolean).join(", ") ||
                    candidate.country ||
                    "—";
                  const caps = candidate.capabilities
                    ? Object.entries(candidate.capabilities)
                        .filter(([, enabled]) => enabled)
                        .map(([name]) => name)
                        .join(" · ")
                    : "voice";
                  return (
                    <TR key={candidate.phone_number}>
                      <TD mono>{formatPhone(candidate.phone_number)}</TD>
                      <TD>{location}</TD>
                      <TD mono className="text-muted">
                        {caps || "—"}
                      </TD>
                      <TD align="right">
                        <Button
                          variant="primary"
                          size="sm"
                          loading={buying === candidate.phone_number}
                          disabled={buying !== null && buying !== candidate.phone_number}
                          onClick={() => void handleBuy(candidate)}
                        >
                          Buy
                        </Button>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          ))}
      </section>

      {/* Release confirmation */}
      <Modal
        open={releaseTarget !== null}
        onClose={() => {
          if (!releasing) setReleaseTarget(null);
        }}
        title="Release number"
        footer={
          <>
            <Button onClick={() => setReleaseTarget(null)} disabled={releasing}>
              Keep it
            </Button>
            <Button variant="danger" loading={releasing} onClick={() => void handleRelease()}>
              Release number
            </Button>
          </>
        }
      >
        <p className="text-sm text-body">
          Release{" "}
          <span className="font-mono text-bright">
            {releaseTarget ? formatPhone(releaseTarget.e164) : ""}
          </span>{" "}
          back to Twilio? Calls to it stop immediately and someone else can buy
          it. This can't be undone.
        </p>
      </Modal>
    </div>
  );
}
